<template>
  <el-dialog
    v-model="dialogVisible"
    :title="`审批设置 - ${$store.getters.getCurrentEnterpriseName || '系统'}`"
    width="75%"
    top="5vh"
    :before-close="handleClose"
    class="config-manager-dialog"
  >
    <!-- 搜索栏和权限提示 -->
    <div class="header-section">
      <el-input
        v-model="searchQuery"
        placeholder="搜索设置项"
        prefix-icon="el-icon-search"
        clearable
        class="search-input"
      />

      <el-alert
        :title="canEdit ? '您有权编辑这些设置' : '您只能查看这些设置'"
        :type="canEdit ? 'success' : 'warning'"
        :closable="false"
        class="permission-alert"
      />
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="5" animated />
    </div>

    <!-- 分类折叠面板 -->
    <div v-else class="config-sections">
      <!-- 报销相关设置 -->
      <el-collapse v-model="activeCollapse">
        <el-collapse-item title="报销审批设置" name="expense">
          <div class="section-content">
            <el-table
              :data="getFilteredConfigsByCategory('expense')"
              :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
              stripe
              border
            >
              <el-table-column
                prop="friendlyName"
                label="设置项"
                width="180"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  <el-tooltip
                    :content="row.friendlyName"
                    placement="top"
                    effect="light"
                  >
                    <span>{{ row.friendlyName }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              <el-table-column prop="value" label="设置值" min-width="300">
                <template #default="{ row }">
                  <div class="value-container">
                    <el-select
                      v-model="row.value"
                      multiple
                      filterable
                      remote
                      reserve-keyword
                      placeholder="请选择"
                      :remote-method="(query) => remoteSearch(query, row)"
                      :loading="row.loading"
                      @change="updateConfig(row)"
                      class="custom-select"
                      :disabled="!canEdit"
                      collapse-tags
                      collapse-tags-tooltip
                    >
                      <el-option
                        v-for="item in row.options"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                      >
                        <div class="option-with-avatar">
                          <el-avatar :size="24" :src="item.avatar"></el-avatar>
                          <span>{{ item.name }}</span>
                        </div>
                      </el-option>
                    </el-select>
                    <el-icon
                      v-if="canEdit"
                      class="add-icon"
                      @click.stop="openContactSelector(row)"
                    >
                      <plus />
                    </el-icon>
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                prop="description"
                label="说明"
                width="180"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  <el-tooltip
                    :content="row.description"
                    placement="top"
                    effect="light"
                  >
                    <span>{{ row.description }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-collapse-item>

        <!-- 付款相关设置 -->
        <el-collapse-item title="付款审批设置" name="payment">
          <div class="section-content">
            <el-table
              :data="getFilteredConfigsByCategory('payment')"
              :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
              stripe
              border
            >
              <el-table-column
                prop="friendlyName"
                label="设置项"
                width="180"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  <el-tooltip
                    :content="row.friendlyName"
                    placement="top"
                    effect="light"
                  >
                    <span>{{ row.friendlyName }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              <el-table-column prop="value" label="设置值" min-width="300">
                <template #default="{ row }">
                  <div class="value-container">
                    <el-select
                      v-model="row.value"
                      multiple
                      filterable
                      remote
                      reserve-keyword
                      placeholder="请选择"
                      :remote-method="(query) => remoteSearch(query, row)"
                      :loading="row.loading"
                      @change="updateConfig(row)"
                      class="custom-select"
                      :disabled="!canEdit"
                      collapse-tags
                      collapse-tags-tooltip
                    >
                      <el-option
                        v-for="item in row.options"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                      >
                        <div class="option-with-avatar">
                          <el-avatar :size="24" :src="item.avatar"></el-avatar>
                          <span>{{ item.name }}</span>
                        </div>
                      </el-option>
                    </el-select>
                    <el-icon
                      v-if="canEdit"
                      class="add-icon"
                      @click.stop="openContactSelector(row)"
                    >
                      <plus />
                    </el-icon>
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                prop="description"
                label="说明"
                width="180"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  <el-tooltip
                    :content="row.description"
                    placement="top"
                    effect="light"
                  >
                    <span>{{ row.description }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-collapse-item>

        <!-- 抄送相关设置 -->
        <el-collapse-item title="抄送人设置" name="cc">
          <div class="section-content">
            <el-table
              :data="getFilteredConfigsByCategory('cc')"
              :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
              stripe
              border
            >
              <el-table-column
                prop="friendlyName"
                label="设置项"
                width="180"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  <el-tooltip
                    :content="row.friendlyName"
                    placement="top"
                    effect="light"
                  >
                    <span>{{ row.friendlyName }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              <el-table-column prop="value" label="设置值" min-width="300">
                <template #default="{ row }">
                  <div class="value-container">
                    <el-select
                      v-model="row.value"
                      multiple
                      filterable
                      remote
                      reserve-keyword
                      placeholder="请选择"
                      :remote-method="(query) => remoteSearch(query, row)"
                      :loading="row.loading"
                      @change="updateConfig(row)"
                      class="custom-select"
                      :disabled="!canEdit"
                      collapse-tags
                      collapse-tags-tooltip
                    >
                      <el-option
                        v-for="item in row.options"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                      >
                        <div class="option-with-avatar">
                          <el-avatar :size="24" :src="item.avatar"></el-avatar>
                          <span>{{ item.name }}</span>
                        </div>
                      </el-option>
                    </el-select>
                    <el-icon
                      v-if="canEdit"
                      class="add-icon"
                      @click.stop="openContactSelector(row)"
                    >
                      <plus />
                    </el-icon>
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                prop="description"
                label="说明"
                width="180"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  <el-tooltip
                    :content="row.description"
                    placement="top"
                    effect="light"
                  >
                    <span>{{ row.description }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-collapse-item>

        <!-- 系统设置 -->
        <el-collapse-item title="系统设置" name="system">
          <div class="section-content">
            <el-table
              :data="getFilteredConfigsByCategory('system')"
              :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
              stripe
              border
            >
              <el-table-column
                prop="friendlyName"
                label="设置项"
                width="180"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  <el-tooltip
                    :content="row.friendlyName"
                    placement="top"
                    effect="light"
                  >
                    <span>{{ row.friendlyName }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              <el-table-column prop="value" label="设置值" min-width="300">
                <template #default="{ row }">
                  <div class="value-container">
                    <el-select
                      v-model="row.value"
                      multiple
                      filterable
                      remote
                      reserve-keyword
                      placeholder="请选择"
                      :remote-method="(query) => remoteSearch(query, row)"
                      :loading="row.loading"
                      @change="updateConfig(row)"
                      class="custom-select"
                      :disabled="!canEdit"
                      collapse-tags
                      collapse-tags-tooltip
                    >
                      <el-option
                        v-for="item in row.options"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                      >
                        <div class="option-with-avatar">
                          <el-avatar :size="24" :src="item.avatar"></el-avatar>
                          <span>{{ item.name }}</span>
                        </div>
                      </el-option>
                    </el-select>
                    <el-icon
                      v-if="canEdit"
                      class="add-icon"
                      @click.stop="openContactSelector(row)"
                    >
                      <plus />
                    </el-icon>
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                prop="description"
                label="说明"
                width="180"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  <el-tooltip
                    :content="row.description"
                    placement="top"
                    effect="light"
                  >
                    <span>{{ row.description }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
  </el-dialog>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import axios from 'axios'
import { useStore } from 'vuex'
import * as ww from '@wecom/jssdk'

// 配置项与分类的映射
const configCategories = {
  // 报销审批相关
  WECOM_FINANCE_APPROVER_ID: 'expense',
  WECOM_CC_APPROVER_ID: 'expense',

  // 付款审批相关
  PAYMENT_WECOM_FINANCE_APPROVER_ID: 'payment',
  PAYMENT_WECOM_THIRD_PARTY_APPROVER_ID: 'payment',
  PAYMENT_WECOM_PROCUREMENT_APPROVER_ID: 'payment',
  PAYMENT_WECOM_REFUND_APPROVER_ID: 'payment',
  PAYMENT_WECOM_REBATE_APPROVER_ID: 'payment',
  PAYMENT_WECOM_PROJECT_APPROVER_ID: 'payment',
  PAYMENT_WECOM_OTHER_APPROVER_ID: 'payment',
  PAYMENT_WECOM_ECOMMERCE_APPROVER_ID: 'payment',
  PAYMENT_WECOM_ONLINE_THIRD_PARTY_APPROVER_ID: 'payment',
  PAYMENT_WECOM_ONLINE_OTHER_APPROVER_ID: 'payment',

  // 抄送人相关
  PAYMENT_WECOM_CC_APPROVER_ID: 'cc',
  PAYMENT_WECOM_THIRD_PARTY_CC_APPROVER_ID: 'cc',
  PAYMENT_WECOM_PROCUREMENT_CC_APPROVER_ID: 'cc',
  PAYMENT_WECOM_REFUND_CC_APPROVER_ID: 'cc',
  PAYMENT_WECOM_REBATE_CC_APPROVER_ID: 'cc',
  PAYMENT_WECOM_PROJECT_CC_APPROVER_ID: 'cc',
  PAYMENT_WECOM_OTHER_CC_APPROVER_ID: 'cc',
  PAYMENT_WECOM_ECOMMERCE_CC_APPROVER_ID: 'cc',
  PAYMENT_WECOM_ONLINE_THIRD_PARTY_CC_APPROVER_ID: 'cc',
  PAYMENT_WECOM_ONLINE_OTHER_CC_APPROVER_ID: 'cc',

  // 系统设置
  CONFIG_MANAGER_USERS: 'system',
}

const configKeyToFriendlyName = {
  WECOM_FINANCE_APPROVER_ID: '报销审批人',
  WECOM_CC_APPROVER_ID: '报销抄送人',
  PAYMENT_WECOM_FINANCE_APPROVER_ID: '付款审批人',
  PAYMENT_WECOM_CC_APPROVER_ID: '付款抄送人',
  PAYMENT_WECOM_THIRD_PARTY_APPROVER_ID: '委外三方支出审批人',
  PAYMENT_WECOM_PROCUREMENT_APPROVER_ID: '物品采购审批人',
  PAYMENT_WECOM_REFUND_APPROVER_ID: '退款审批人',
  PAYMENT_WECOM_REBATE_APPROVER_ID: 'FY审批人',
  PAYMENT_WECOM_PROJECT_APPROVER_ID: '(投/中标)项目付款审批人',
  PAYMENT_WECOM_OTHER_APPROVER_ID: '其他付款审批人',
  PAYMENT_WECOM_ECOMMERCE_APPROVER_ID: '线上及电商运营审批人',
  PAYMENT_WECOM_ONLINE_THIRD_PARTY_APPROVER_ID: '线上三方支出审批人',
  PAYMENT_WECOM_ONLINE_OTHER_APPROVER_ID: '线上其他支出审批人',
  PAYMENT_WECOM_THIRD_PARTY_CC_APPROVER_ID: '委外三方支出抄送人',
  PAYMENT_WECOM_PROCUREMENT_CC_APPROVER_ID: '物品采购抄送人',
  PAYMENT_WECOM_REFUND_CC_APPROVER_ID: '退款抄送人',
  PAYMENT_WECOM_REBATE_CC_APPROVER_ID: 'FY抄送人',
  PAYMENT_WECOM_PROJECT_CC_APPROVER_ID: '(投/中标)项目付款抄送人',
  PAYMENT_WECOM_OTHER_CC_APPROVER_ID: '其它付款抄送人',
  PAYMENT_WECOM_ECOMMERCE_CC_APPROVER_ID: '线上及电商运营抄送人',
  PAYMENT_WECOM_ONLINE_THIRD_PARTY_CC_APPROVER_ID: '线上三方支出抄送人',
  PAYMENT_WECOM_ONLINE_OTHER_CC_APPROVER_ID: '线上其他支出抄送人',
  CONFIG_MANAGER_USERS: '配置管理员',
}

export default {
  name: 'ConfigManagerDialog',
  props: {
    visible: Boolean,
  },
  emits: ['update:visible'],
  components: {
    Plus,
  },
  setup(props, { emit }) {
    const store = useStore()
    const configs = ref([])
    const dialogVisible = ref(props.visible)
    const searchQuery = ref('')
    const loading = ref(false)
    const wwInitialized = ref(false)
    const canEdit = ref(false)
    const configManagers = ref([])
    const activeCollapse = ref(['expense']) // 默认打开报销审批设置面板

    // 根据搜索过滤配置项
    const filteredConfigs = computed(() => {
      if (!searchQuery.value) return configs.value
      const lowercaseQuery = searchQuery.value.toLowerCase()
      return configs.value.filter(
        (config) =>
          (config.friendlyName &&
            config.friendlyName.toLowerCase().includes(lowercaseQuery)) ||
          (config.description &&
            config.description.toLowerCase().includes(lowercaseQuery)) ||
          (Array.isArray(config.value) &&
            config.value.some(
              (v) =>
                v && v.name && v.name.toLowerCase().includes(lowercaseQuery)
            ))
      )
    })

    // 根据分类筛选配置项
    const getFilteredConfigsByCategory = (category) => {
      // 先根据搜索条件过滤
      const filtered = filteredConfigs.value
      // 如果有搜索条件，则打开所有面板
      if (searchQuery.value && !activeCollapse.value.includes(category)) {
        activeCollapse.value.push(category)
      }
      // 再根据分类过滤
      return filtered.filter(
        (config) => configCategories[config.key] === category
      )
    }

    watch(
      () => props.visible,
      (newValue) => {
        dialogVisible.value = newValue
        if (newValue) {
          resetData()
          fetchConfigs()
        }
      }
    )

    watch(dialogVisible, (newValue) => {
      emit('update:visible', newValue)
    })

    // 监听搜索，当搜索时展开所有面板
    watch(searchQuery, (newValue) => {
      if (newValue) {
        activeCollapse.value = ['expense', 'payment', 'cc', 'system']
      }
    })

    const resetData = () => {
      configs.value = []
      searchQuery.value = ''
      canEdit.value = false
      configManagers.value = []
      activeCollapse.value = ['expense']
    }

    const fetchConfigs = async () => {
      loading.value = true
      try {
        // 获取当前企业配置，并在API调用中传递企业标识符（英文）
        const enterpriseIdentifier = store.getters.getCurrentEnterpriseIdentifier
        const enterpriseName = store.getters.getCurrentEnterpriseName
        console.log(`📋 获取企业配置: ${enterpriseName} (${enterpriseIdentifier})`)

        const response = await axios.get(
          store.getters.baseUrl + '/get_all_configs',
          {
            headers: {
              Authorization: `Bearer ${store.state.accessToken}`,
              'X-Enterprise-Identifier': enterpriseIdentifier,  // 使用英文标识符
            },
          }
        )
        if (response.data.code === 200) {
          const data = response.data.data
          configs.value = data.configs.map((config) => {
            let value, options
            if (Array.isArray(config.value)) {
              value = config.value.map((user) => user.id)
              options = config.value
            } else if (config.key === 'CONFIG_MANAGER_USERS') {
              value = config.value.split(',')
              options = data.config_managers
            } else {
              value = config.value
              options = []
            }
            return {
              key: config.key,
              friendlyName: configKeyToFriendlyName[config.key] || config.key,
              value: value,
              options: options,
              description: config.description,
              loading: false,
              category: configCategories[config.key] || 'system',
            }
          })
          canEdit.value = data.canEdit
          configManagers.value = data.config_managers
        } else {
          ElMessage.error('获取配置信息失败')
        }
      } catch (error) {
        ElMessage.error('获取配置信息失败')
        console.error('错误信息:', error)
      } finally {
        loading.value = false
      }
    }

    const remoteSearch = async (query, row) => {
      if (query) {
        row.loading = true
        try {
          const enterpriseIdentifier = store.getters.getCurrentEnterpriseIdentifier
          const response = await axios.post(
            store.getters.baseUrl + '/search_users',
            { query },
            {
              headers: {
                Authorization: `Bearer ${store.state.accessToken}`,
                'X-Enterprise-Identifier': enterpriseIdentifier,  // 使用英文标识符
              },
            }
          )
          row.options = response.data.users.map((user) => ({
            id: user.userid,
            name: user.name,
            avatar: user.avatar,
          }))
        } catch (error) {
          console.error('搜索用户失败:', error)
          ElMessage.error('搜索用户失败')
        } finally {
          row.loading = false
        }
      } else {
        row.options = []
      }
    }

    const initWechatSDK = async () => {
      try {
        // 注册 @wecom/jssdk，提供额外的第三方应用身份信息
        await ww.register({
          beta: true,
          debug: true,
          corpId: store.getters.getCorpId,
          agentId: store.getters.getExpenseAgentId,
          jsApiList: [
            'selectEnterpriseContact',
            'openUserProfile',
            'checkJsApi',
          ],
          getConfigSignature: getConfigSignature,
          getAgentConfigSignature: getAgentConfigSignature,
        })
        wwInitialized.value = true
      } catch (error) {
        console.error('初始化微信 JS-SDK 失败：', error)
      }
    }

    const getConfigSignature = async (url) => {
      try {
        const response = await axios.get(
          store.getters.baseUrl + '/wecom_signature',
          { params: { url } }
        )
        const configSignature = response.data.data
        return {
          timestamp: configSignature.timestamp,
          nonceStr: configSignature.noncestr, // 使用 noncestr 字段
          signature: configSignature.signature,
        }
      } catch (error) {
        console.error('获取微信 JS-SDK 配置信息失败：', error)
        throw error
      }
    }

    const getAgentConfigSignature = async (url) => {
      try {
        const response = await axios.get(
          store.getters.baseUrl + '/agent_signature',
          { params: { url } }
        )
        const agentConfigSignature = response.data.data
        return {
          timestamp: agentConfigSignature.timestamp,
          nonceStr: agentConfigSignature.noncestr,
          signature: agentConfigSignature.signature,
        }
      } catch (error) {
        console.error('获取第三方应用签名失败：', error)
        throw error
      }
    }

    const openContactSelector = async (row) => {
      if (!wwInitialized.value) {
        ElMessage.error('企业微信 SDK 未初始化')
        return
      }

      try {
        const res = await ww.selectEnterpriseContact({
          fromDepartmentId: -1,
          mode: 'single',
          type: ['user'],
        })

        const selectedUser = res.result.userList && res.result.userList[0]
        if (selectedUser) {
          const existingUser = row.options.find(
            (user) => user.id === selectedUser.id
          )
          if (!existingUser) {
            const newUser = {
              id: selectedUser.id || '',
              name: selectedUser.name || '',
              avatar: selectedUser.avatar || '',
            }
            row.options.push(newUser)
            if (!row.value) row.value = []
            row.value.push(newUser.id)
            updateConfig(row)
          } else {
            ElMessage.warning('该用户已存在')
          }
        }
      } catch (error) {
        if (
          error.errMsg &&
          error.errMsg.indexOf('selectEnterpriseContact:cancel') === -1
        ) {
          console.error('选择用户失败:', error)
          ElMessage.error('选择用户失败')
        }
      }
    }

    const updateConfig = async (config) => {
      if (!canEdit.value) {
        ElMessage.warning('您没有修改权限')
        return
      }
      try {
        const enterpriseIdentifier = store.getters.getCurrentEnterpriseIdentifier
        const enterpriseName = store.getters.getCurrentEnterpriseName
        console.log(`🔄 更新企业 ${enterpriseName} (${enterpriseIdentifier}) 的配置: ${config.key}`)

        const response = await axios.post(
          store.getters.baseUrl + '/update_config',
          {
            key: config.key,
            value: config.value.join(','),
          },
          {
            headers: {
              Authorization: `Bearer ${store.state.accessToken}`,
              'X-Enterprise-Identifier': enterpriseIdentifier,  // 使用英文标识符
            },
          }
        )

        if (response.data.enterprise) {
          ElMessage.success(`${response.data.enterprise} 企业设置更新成功`)
        } else {
          ElMessage.success('设置更新成功')
        }
      } catch (error) {
        ElMessage.error('设置更新失败')
        console.error(error)
      }
    }

    const handleClose = () => {
      emit('update:visible', false)
    }

    onMounted(() => {
      if (props.visible) {
        fetchConfigs()
      }
      initWechatSDK()
    })

    return {
      configs,
      filteredConfigs,
      getFilteredConfigsByCategory,
      updateConfig,
      dialogVisible,
      handleClose,
      searchQuery,
      remoteSearch,
      loading,
      openContactSelector,
      Plus,
      canEdit,
      configManagers,
      activeCollapse,
    }
  },
}
</script>

<style scoped>
.config-manager-dialog {
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB',
    'Microsoft YaHei', '微软雅黑', Arial, sans-serif;
}

.config-manager-dialog :deep(.el-dialog__body) {
  padding: 20px 30px;
}

.header-section {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
}

.search-input {
  flex: 1;
  max-width: 400px;
}

.permission-alert {
  flex: 1;
}

.loading-container {
  padding: 20px 0;
}

.config-sections {
  margin-bottom: 20px;
}

.section-content {
  padding: 10px 0;
}

.value-container {
  display: flex;
  align-items: center;
  position: relative;
}

.custom-select {
  width: 100%;
}

.add-icon {
  cursor: pointer;
  font-size: 16px;
  color: #409eff;
  transition: all 0.3s;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.add-icon:hover {
  color: #66b1ff;
  transform: translateY(-50%) scale(1.1);
}

.custom-select :deep(.el-input__inner) {
  padding-right: 40px;
}

.custom-select :deep(.el-input.is-disabled .el-input__inner) {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
  color: #c0c4cc;
  cursor: not-allowed;
}

.config-managers {
  margin-top: 20px;
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.config-managers h3 {
  margin-bottom: 10px;
  font-size: 16px;
  color: #606266;
}

.managers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.option-with-avatar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.el-table {
  --el-table-header-bg-color: #f5f7fa;
  --el-table-border-color: #ebeef5;
}

:deep(.el-collapse-item__header) {
  font-size: 16px;
  font-weight: 500;
  padding: 0 10px;
}

:deep(.el-collapse-item__content) {
  padding: 10px;
}
</style>
